import React from "react";
import { Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tabs, Tab } from "@material-ui/core";
import moment from "moment";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "react-simple-snackbar";
import Spinner from "../components/Spinner/Spinner";
import NoData from "../components/svgIcons/NoData";
import styles from "../components/Invoices-DataGrid/Invoices.module.css";
import { useHistory } from "react-router-dom";
import {
  getDeletedInvoices,
  restoreInvoiceById,
  deleteInvoicePermanentById,
} from "../actions/invoiceActions";
import {
  getDeletedOrders,
  restoreOrderById,
  deleteOrderPermanentById,
} from "../actions/orderActions";

const statuses = ["Unpaid", "Paid", "Partial", "Booked"];

const RecentlyDeletedPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const { deletedInvoices = [], isLoading: invoicesLoading } = useSelector(
    (state) => state.invoices || {}
  );
  const { deletedOrders = [], isLoading: ordersLoading } = useSelector(
    (state) => state.orders || {}
  );
  const [activeTab, setActiveTab] = React.useState(0);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = React.useState(null);
  const isLoading = invoicesLoading || ordersLoading;

  React.useEffect(() => {
    dispatch(getDeletedInvoices());
    dispatch(getDeletedOrders());
  }, [dispatch]);

  const toCommas = (value = 0) =>
    Number(value)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const checkStatus = (status) =>
    status === "Partial"
      ? {
          border: "solid 0px #1976d2",
          backgroundColor: "#baddff",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : status === "Paid"
      ? {
          border: "solid 0px green",
          backgroundColor: "#a5ffcd",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : status === "Unpaid"
      ? {
          border: "solid 0px red",
          backgroundColor: "#ffaa91",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : status === "Booked"
      ? {
          border: "solid 0px #92400e",
          backgroundColor: "#fef3c7",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : {
          border: "solid 0px #94a3b8",
          backgroundColor: "#e2e8f0",
          padding: "8px 18px",
          borderRadius: "20px",
        };

  const handleRestoreInvoice = (id) => {
    dispatch(restoreInvoiceById(id, openSnackbar)).then(() =>
      dispatch(getDeletedInvoices())
    );
  };

  const handleRestoreOrder = (id) => {
    dispatch(restoreOrderById(id, openSnackbar)).then(() =>
      dispatch(getDeletedOrders())
    );
  };

  const handlePermanentDelete = () => {
    if (!permanentDeleteTarget?.id) return;
    if (permanentDeleteTarget.type === "invoice") {
      dispatch(deleteInvoicePermanentById(permanentDeleteTarget.id, openSnackbar)).then(() => {
        dispatch(getDeletedInvoices());
      });
    }
    if (permanentDeleteTarget.type === "order") {
      dispatch(deleteOrderPermanentById(permanentDeleteTarget.id, openSnackbar)).then(() => {
        dispatch(getDeletedOrders());
      });
    }
    setPermanentDeleteTarget(null);
  };

  const openInvoice = (id) => history.push(`/invoice/${id}`);
  const openOrder = (id) => history.push(`/orders/${id}`);

  const invoiceColumns = [
    {
      field: "client",
      headerName: "Client",
      type: "singleSelect",
      valueOptions: deletedInvoices
        .map((inv) => inv?.client?.store)
        .filter(Boolean),
      editable: false,
      minWidth: 125,
      flex: 1,
      valueGetter: (params) => params.row?.client?.store || "Unknown",
    },
    {
      field: "total",
      headerName: "Amount",
      editable: false,
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => `$${toCommas(params.value || 0)}`,
    },
    {
      field: "receivedByDate",
      headerName: "Received Date",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
    },
    {
      field: "status",
      type: "singleSelect",
      valueOptions: statuses,
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <button style={checkStatus(params.value)}>{params.value}</button>
      ),
    },
    {
      field: "deletedAt",
      headerName: "Deleted On",
      type: "date",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
      valueFormatter: ({ value }) =>
        value ? moment(value).format("MMM DD, YYYY") : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            style={{ color: "white", backgroundColor: "green" }}
            onClick={(e) => {
              e.stopPropagation();
              handleRestoreInvoice(params.row._id);
            }}
            variant="contained"
          >
            Restore
          </Button>
          <Button
            style={{ color: "white", backgroundColor: "red" }}
            onClick={(e) => {
              e.stopPropagation();
              setPermanentDeleteTarget({ type: "invoice", id: params.row._id });
            }}
            variant="contained"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const orderColumns = [
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.value || "—",
    },
    {
      field: "pcInvoiceNumber",
      headerName: "PC Invoice #",
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.value || "—",
    },
    {
      field: "clientPONumber",
      headerName: "Client PO #",
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.value || "—",
    },
    {
      field: "shipTo",
      headerName: "Ship To",
      minWidth: 160,
      flex: 1,
      valueGetter: (params) => params.row?.shipTo?.name || "—",
    },
    {
      field: "total",
      headerName: "Amount",
      editable: false,
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => `$${toCommas(params.value || 0)}`,
    },
    {
      field: "receivedByDate",
      headerName: "Ship Date",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
    },
    {
      field: "status",
      type: "singleSelect",
      valueOptions: statuses,
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <button style={checkStatus(params.value)}>{params.value}</button>
      ),
    },
    {
      field: "deletedAt",
      headerName: "Deleted On",
      type: "date",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
      valueFormatter: ({ value }) =>
        value ? moment(value).format("MMM DD, YYYY") : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            style={{ color: "white", backgroundColor: "green" }}
            onClick={(e) => {
              e.stopPropagation();
              handleRestoreOrder(params.row._id);
            }}
            variant="contained"
          >
            Restore
          </Button>
          <Button
            style={{ color: "white", backgroundColor: "red" }}
            onClick={(e) => {
              e.stopPropagation();
              setPermanentDeleteTarget({ type: "order", id: params.row._id });
            }}
            variant="contained"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const renderNoData = (message) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      style={{ paddingTop: 20, margin: 80 }}
    >
      <NoData />
      <p style={{ padding: 40, color: "gray", textAlign: "center" }}>
        {message}
      </p>
    </Box>
  );

  const renderGrid = ({ rows, columns, onRowClick }) => (
    <div className={styles.gridLayout}>
      <DataGrid
        sx={{
          "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
            py: "6px",
          },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "8px",
          },
          "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
            py: "12px",
          },
        }}
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        pageSize={20}
        components={{ Toolbar: GridToolbar }}
        disableSelectionOnClick
        onRowClick={(row) => onRowClick(row.id)}
        sortingOrder={["desc", "asc"]}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "deletedAt",
                sort: "desc",
              },
            ],
          },
        }}
      />
    </div>
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ paddingTop: 40 }}
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <div className={styles.pageLayout}>
      <Container style={{ maxWidth: 1600 }}>
        <div className={styles.buttons}>
          <Typography variant="h4" component="h1" style={{ paddingTop: 10 }}>
            Recently Deleted
          </Typography>
        </div>
        <Tabs
          value={activeTab}
          onChange={(event, nextTab) => setActiveTab(nextTab)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab label={`Invoices (${deletedInvoices.length})`} />
          <Tab label={`Orders (${deletedOrders.length})`} />
        </Tabs>
        {activeTab === 0 && (
          deletedInvoices.length
            ? renderGrid({ rows: deletedInvoices, columns: invoiceColumns, onRowClick: openInvoice })
            : renderNoData("No invoices have been deleted recently.")
        )}
        {activeTab === 1 && (
          deletedOrders.length
            ? renderGrid({ rows: deletedOrders, columns: orderColumns, onRowClick: openOrder })
            : renderNoData("No orders have been deleted recently.")
        )}
      </Container>
      <Dialog
        open={Boolean(permanentDeleteTarget)}
        onClose={() => setPermanentDeleteTarget(null)}
      >
        <DialogTitle>
          Delete {permanentDeleteTarget?.type === "order" ? "Order" : "Invoice"} Permanently
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will remove the {permanentDeleteTarget?.type || "record"} permanently from the system. This cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteTarget(null)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handlePermanentDelete}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RecentlyDeletedPage;
